import React, { useEffect, useState, useCallback, useRef } from 'react';
import { collection, doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { trackEvent, trackPageView } from '../utils/analytics';
import { usePageMeta } from '../utils/usePageMeta';
import {
  COLLECTION_MORNING_REGISTRATIONS,
  COLLECTION_MORNING_CAPACITY,
  MorningRegistrationDoc,
} from '../constants/firestore';
import {
  MORNING_CONFIG,
  MORNING_EVENTS,
  MORNING_FAQ,
  MAX_CAPACITY,
  FEW_SPOTS_THRESHOLD,
  MorningEvent,
  EventStatus,
} from '../constants/morning';
import { SubPageNav, SubPageFoot, ArrowIcon } from '../components/SubPageChrome';
import playerImg from '../assets/tempo_player1.webp';
import './MorningLandingPage.css';
import './HomePage.css';

// ============================================================================
// Utilities
// ============================================================================

function getUtmParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source'),
    utm_medium: p.get('utm_medium'),
    utm_campaign: p.get('utm_campaign'),
    utm_content: p.get('utm_content'),
    utm_term: p.get('utm_term'),
    fbclid: p.get('fbclid'),
    gclid: p.get('gclid'),
  };
}

function getDeviceType(): string {
  const w = window.innerWidth;
  return w <= 768 ? 'mobile' : w <= 1024 ? 'tablet' : 'desktop';
}

function getNextWeekday(dayName: string): Date {
  const map: Record<string, number> = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
  };
  const target = map[dayName];
  const now = new Date();
  let diff = target - now.getDay();
  if (diff <= 0) diff += 7;
  const next = new Date(now);
  next.setDate(now.getDate() + diff);
  return next;
}

function pad2(n: number) { return String(n).padStart(2, '0'); }

function getCalendarUrl(event: MorningEvent): string {
  const d = getNextWeekday(event.day);
  const ymd = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
  const start = `${ymd}T${pad2(event.startHour)}${pad2(event.startMin)}00`;
  const end = `${ymd}T${pad2(event.endHour)}${pad2(event.endMin)}00`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `${event.name} at TEMPO`,
    dates: `${start}/${end}`,
    location: MORNING_CONFIG.address,
    details: `${event.body}\n\nArrive 15 minutes early.\nAddress: ${MORNING_CONFIG.address}\nPhone: ${MORNING_CONFIG.phone}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function getISOWeekKey(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

// ============================================================================
// Registration / Waitlist Modal
// ============================================================================

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: MorningEvent | null;
  onCapacityUpdate: (eventId: string, newCount: number) => void;
}

const RegistrationModal: React.FC<RegistrationModalProps> = ({ isOpen, onClose, event, onCapacityUpdate }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    experience: '' as '' | 'first_timer' | 'played_few_times' | 'regular_player',
    consent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [resultType, setResultType] = useState<'registration' | 'waitlist' | null>(null);
  const formStartedRef = useRef(false);

  const isWaitlist = event?.status === 'sold_out' || event?.status === 'waitlist';

  // ---- validation ----
  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.phone.trim()) {
      e.phone = 'Phone number is required';
    } else if (!/^[\d\s()+-]{7,}$/.test(formData.phone.trim())) {
      e.phone = 'Please enter a valid phone number';
    }
    if (!formData.email.trim()) {
      e.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      e.email = 'Please enter a valid email';
    }
    if (!formData.experience) e.experience = 'Please select your experience level';
    if (!formData.consent) e.consent = 'Please agree to continue';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---- submit ----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !event) return;
    setIsSubmitting(true);

    try {
      const utm = getUtmParams();
      const weekKey = getISOWeekKey();
      const counterRef = doc(db, COLLECTION_MORNING_CAPACITY, weekKey);
      const max = MAX_CAPACITY[event.id] || 999;

      const result = await runTransaction(db, async (transaction) => {
        const counterSnap = await transaction.get(counterRef);
        const currentCount = counterSnap.exists()
          ? (counterSnap.data()[event.id] || 0)
          : 0;

        const isFull = currentCount >= max;
        const regType = (isWaitlist || isFull) ? 'waitlist' : 'registration';

        const regDoc: Omit<MorningRegistrationDoc, 'created_at'> & { created_at: any } = {
          type: regType,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim().toLowerCase(),
          experience: formData.experience as MorningRegistrationDoc['experience'],
          consent: formData.consent,
          event_id: event.id,
          event_type: event.eventType,
          event_day: event.day,
          event_time: event.time,
          utm_source: utm.utm_source,
          utm_medium: utm.utm_medium,
          utm_campaign: utm.utm_campaign,
          utm_content: utm.utm_content,
          utm_term: utm.utm_term,
          fbclid: utm.fbclid,
          gclid: utm.gclid,
          referrer: document.referrer || null,
          device_type: getDeviceType(),
          created_at: serverTimestamp(),
          source: 'morning_landing_page',
        };

        const regRef = doc(collection(db, COLLECTION_MORNING_REGISTRATIONS));
        transaction.set(regRef, regDoc);

        if (regType === 'registration') {
          if (counterSnap.exists()) {
            transaction.update(counterRef, { [event.id]: currentCount + 1 });
          } else {
            transaction.set(counterRef, { [event.id]: 1, week_start: weekKey });
          }
        }

        return { regType, newCount: regType === 'registration' ? currentCount + 1 : currentCount };
      });

      if (result.regType === 'registration') {
        onCapacityUpdate(event.id, result.newCount);
      }

      const eventName = result.regType === 'waitlist' ? 'waitlist_join' : 'form_submit';
      trackEvent(eventName, {
        event_id: event.id,
        event_type: event.eventType,
        ...utm,
      });
      if (result.regType === 'registration') {
        trackEvent('registration_complete', {
          event_id: event.id,
          event_type: event.eventType,
          value: event.price || '0',
        });
      }
      setResultType(result.regType as 'registration' | 'waitlist');
      setIsSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setErrors({ submit: 'Something went wrong. Please try again or call us.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- field change ----
  const set = (field: string, value: any) => {
    if (!formStartedRef.current && event) {
      formStartedRef.current = true;
      trackEvent('form_start', { event_id: event.id });
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---- close & reset ----
  const handleClose = () => {
    if (!isSuccess && event && formStartedRef.current) {
      trackEvent('form_close', { event_id: event.id, had_input: true });
    } else if (!isSuccess && event) {
      trackEvent('form_close', { event_id: event.id, had_input: false });
    }
    setFormData({ firstName: '', lastName: '', phone: '', email: '', experience: '', consent: false });
    setErrors({});
    setIsSuccess(false);
    setResultType(null);
    formStartedRef.current = false;
    onClose();
  };

  // ---- scroll lock + form_view ----
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (event) trackEvent('form_view', { event_id: event.id, source_section: 'schedule' });
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, event]);

  if (!isOpen || !event) return null;

  // ---- success state ----
  if (isSuccess) {
    return (
      <div className="v2-modal-overlay" onClick={handleClose} role="dialog" aria-modal="true">
        <div className="v2-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="v2-modal-close" onClick={handleClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>

          <div className="v2-modal-success-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <h2>{resultType === 'waitlist' ? "You're on the waitlist." : "You're registered."}</h2>
          <p className="v2-modal-success-event">{event.day} &middot; {event.time} &middot; {event.name}</p>

          {resultType !== 'waitlist' && (
            <div className="v2-modal-success-list">
              <div className="row">
                <strong>Address</strong>
                <a href={MORNING_CONFIG.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                  {MORNING_CONFIG.address}
                </a>
              </div>
              <div className="row">
                <strong>Arrive</strong>
                <span>15 minutes before your session.</span>
              </div>
              <div className="row">
                <strong>What to bring</strong>
                <span>Athletic clothes &amp; court shoes. We provide rackets &amp; balls.</span>
              </div>
            </div>
          )}

          <p className="v2-modal-success-note">
            {resultType === 'waitlist'
              ? "We'll reach out as soon as a spot opens up."
              : `We'll send you an email to confirm your spot. If you don't see it, call us at ${MORNING_CONFIG.phone}.`}
          </p>

          <div className="v2-modal-actions">
            {resultType !== 'waitlist' && (
              <a
                href={getCalendarUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
                className="v2-btn v2-btn-ghost"
                onClick={() => trackEvent('calendar_add_click', { event_id: event.id })}
              >
                Add to Calendar
              </a>
            )}
            <a href={`tel:${MORNING_CONFIG.phone}`} className="v2-btn v2-btn-ghost">
              Call / Text Us
            </a>
          </div>

          <button className="v2-btn v2-btn-block" onClick={handleClose} style={{ marginTop: 16 }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  // ---- form ----
  const submitLabel = isWaitlist ? 'Join Waitlist' : event.submitText;

  return (
    <div className="v2-modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="v2-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="v2-modal-close" onClick={handleClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        <div className="v2-modal-summary">
          <span>{event.day}</span>
          <span>{event.time}</span>
          <span>{event.name}</span>
        </div>

        <h2 id="modal-title">{isWaitlist ? 'Join the waitlist.' : event.headline}</h2>
        <p className="v2-modal-subtext">{event.microcopy}</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="v2-form-grid">
            <div className="v2-form-row">
              <label htmlFor="reg-first">First Name *</label>
              <input
                type="text"
                id="reg-first"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                className={errors.firstName ? 'err' : ''}
                disabled={isSubmitting}
              />
              {errors.firstName && <span className="err">{errors.firstName}</span>}
            </div>
            <div className="v2-form-row">
              <label htmlFor="reg-last">Last Name *</label>
              <input
                type="text"
                id="reg-last"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className={errors.lastName ? 'err' : ''}
                disabled={isSubmitting}
              />
              {errors.lastName && <span className="err">{errors.lastName}</span>}
            </div>

            <div className="v2-form-row full">
              <label htmlFor="reg-phone">Mobile Phone *</label>
              <input
                type="tel"
                id="reg-phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={errors.phone ? 'err' : ''}
                disabled={isSubmitting}
              />
              {errors.phone && <span className="err">{errors.phone}</span>}
            </div>

            <div className="v2-form-row full">
              <label htmlFor="reg-email">Email *</label>
              <input
                type="email"
                id="reg-email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => set('email', e.target.value)}
                className={errors.email ? 'err' : ''}
                disabled={isSubmitting}
              />
              {errors.email && <span className="err">{errors.email}</span>}
            </div>

            <div className="v2-form-row full">
              <label htmlFor="reg-experience">Experience Level *</label>
              <select
                id="reg-experience"
                value={formData.experience}
                onChange={(e) => set('experience', e.target.value)}
                className={errors.experience ? 'err' : ''}
                disabled={isSubmitting}
              >
                <option value="" disabled>Select one</option>
                <option value="first_timer">First timer</option>
                <option value="played_few_times">Played a few times</option>
                <option value="regular_player">Regular player</option>
              </select>
              {errors.experience && <span className="err">{errors.experience}</span>}
            </div>
          </div>

          <label className="v2-form-consent">
            <input
              type="checkbox"
              checked={formData.consent}
              onChange={(e) => set('consent', e.target.checked)}
              disabled={isSubmitting}
            />
            <span>I agree to receive SMS/email communications about this event.</span>
          </label>
          {errors.consent && <span className="err" style={{ color: '#a23', fontSize: 12.5 }}>{errors.consent}</span>}

          {errors.submit && <div className="v2-form-error-banner">{errors.submit}</div>}

          <button type="submit" className="v2-btn v2-btn-block" disabled={isSubmitting} style={{ marginTop: 24 }}>
            {isSubmitting ? 'Submitting\u2026' : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// FAQ Accordion Item
// ============================================================================

interface FaqItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  id: string;
}

const FaqItem: React.FC<FaqItemProps> = ({ question, answer, isOpen, onToggle, id }) => (
  <div className={`faq-item ${isOpen ? 'faq-item--open' : ''}`}>
    <button className="faq-question" onClick={onToggle} aria-expanded={isOpen} type="button">
      <span>{question}</span>
      <svg className="faq-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d={isOpen ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'} />
      </svg>
    </button>
    <div className="faq-answer" aria-hidden={!isOpen}>
      <p>{answer}</p>
    </div>
  </div>
);

// ============================================================================
// Main Page
// ============================================================================

const MorningLandingPage: React.FC = () => {
  usePageMeta({
    title: 'Morning Padel in Houston | Free Beginner Sessions at TEMPO',
    description: "TEMPO's padel sessions in Houston. Free intros Wednesday 9:00–9:45 AM and 6:00–7:00 PM, Thursday 6:00–7:00 PM, Saturday 9:00–10:00 AM. Houston local time. No experience needed.",
    canonicalPath: '/morning',
  });
  const [selectedEvent, setSelectedEvent] = useState<MorningEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [capacityCounts, setCapacityCounts] = useState<Record<string, number>>({});
  const [capacityLoaded, setCapacityLoaded] = useState(false);
  const sectionsFiredRef = useRef<Set<string>>(new Set());

  // ---- derive live event statuses from capacity counters ----
  const liveEvents: MorningEvent[] = MORNING_EVENTS.map((ev) => {
    if (!capacityLoaded) return ev;
    const count = capacityCounts[ev.id] || 0;
    const max = MAX_CAPACITY[ev.id] || 999;
    const remaining = max - count;

    let status: EventStatus;
    let spotsLeft: number | undefined;

    if (remaining <= 0) {
      status = 'waitlist';
    } else if (remaining <= FEW_SPOTS_THRESHOLD) {
      status = 'few_spots';
      spotsLeft = remaining;
    } else {
      status = 'available';
    }

    return { ...ev, status, spotsLeft };
  });

  const handleCapacityUpdate = useCallback((eventId: string, newCount: number) => {
    setCapacityCounts((prev) => ({ ...prev, [eventId]: newCount }));
  }, []);

  // ---- fetch capacity on mount ----
  useEffect(() => {
    const fetchCapacity = async () => {
      try {
        const weekKey = getISOWeekKey();
        const counterRef = doc(db, COLLECTION_MORNING_CAPACITY, weekKey);
        const snap = await getDoc(counterRef);
        if (snap.exists()) {
          const data = snap.data();
          const counts: Record<string, number> = {};
          for (const eventId of Object.keys(MAX_CAPACITY)) {
            counts[eventId] = typeof data[eventId] === 'number' ? data[eventId] : 0;
          }
          setCapacityCounts(counts);
        }
      } catch (err) {
        console.error('Failed to fetch capacity:', err);
      }
      setCapacityLoaded(true);
    };
    fetchCapacity();
  }, []);

  // ---- page-level setup ----
  useEffect(() => {
    document.title = 'Morning Padel in Houston | Free Beginner Sessions at TEMPO';
    trackPageView('Morning Padel in Houston | Free Beginner Sessions at TEMPO', '/morning');

    const description =
      "TEMPO's padel sessions in Houston. Free intros Wednesday 9:00–9:45 AM and 6:00–7:00 PM, Thursday 6:00–7:00 PM, Saturday 9:00–10:00 AM. Houston local time. No experience needed.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (meta) {
      meta.content = description;
    } else {
      meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }
  }, []);

  // ---- section visibility tracking (fires once per section per page load) ----
  useEffect(() => {
    const sectionIds = [
      'morning-schedule',
      'why-mornings-title',
      'padel-title',
      'tempo-title',
      'testimonials-title',
      'faq-title',
      'final-cta-title',
    ];
    const sectionNames: Record<string, string> = {
      'morning-schedule': 'schedule',
      'why-mornings-title': 'why_mornings',
      'padel-title': 'what_is_padel',
      'tempo-title': 'why_tempo',
      'testimonials-title': 'social_proof',
      'faq-title': 'faq',
      'final-cta-title': 'final_cta',
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !sectionsFiredRef.current.has(entry.target.id)) {
            sectionsFiredRef.current.add(entry.target.id);
            trackEvent('section_view', { section: sectionNames[entry.target.id] });
          }
        });
      },
      { threshold: 0.3 },
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // ---- handlers ----
  const scrollToSchedule = useCallback(() => {
    document.getElementById('morning-schedule')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleHeroCta = useCallback(() => {
    trackEvent('hero_cta_click', { cta_label: 'Reserve My Morning Spot', placement: 'hero' });
    scrollToSchedule();
  }, [scrollToSchedule]);

  const handleHeroSecondary = useCallback(() => {
    trackEvent('hero_cta_click', { cta_label: 'See the Schedule', placement: 'hero' });
    scrollToSchedule();
  }, [scrollToSchedule]);

  const openRegistration = useCallback((event: MorningEvent) => {
    trackEvent('schedule_card_click', {
      event_id: event.id,
      event_type: event.eventType,
      event_day: event.day,
      event_time: event.time,
      placement: 'schedule',
    });
    setSelectedEvent(event);
    setIsModalOpen(true);
  }, []);

  const handleFaqToggle = useCallback((idx: number, question: string) => {
    setOpenFaqIndex((prev) => {
      const opening = prev !== idx;
      if (opening) trackEvent('faq_expand', { question_id: `faq_${idx}`, question });
      return opening ? idx : null;
    });
  }, []);

  const handlePhoneClick = useCallback((placement: string) => {
    trackEvent('phone_click', { placement });
  }, []);

  const handleStickyCta = useCallback(() => {
    trackEvent('hero_cta_click', { cta_label: 'Reserve My Morning Spot', placement: 'sticky_mobile' });
    scrollToSchedule();
  }, [scrollToSchedule]);

  const openRegistrationFromFinalCta = useCallback((event: MorningEvent) => {
    trackEvent('schedule_card_click', {
      event_id: event.id,
      event_type: event.eventType,
      event_day: event.day,
      event_time: event.time,
      placement: 'final_cta',
    });
    setSelectedEvent(event);
    setIsModalOpen(true);
  }, []);

  // ---- render ----
  const statusClass = (ev: MorningEvent): string => {
    if (ev.status === 'sold_out' || ev.status === 'waitlist') return 'full';
    if (ev.status === 'few_spots') return 'few';
    return 'open';
  };
  const statusText = (ev: MorningEvent): string => {
    if (ev.status === 'sold_out' || ev.status === 'waitlist') return 'FULL';
    if (ev.status === 'few_spots') return ev.spotsLeft ? `${ev.spotsLeft} LEFT` : 'FEW LEFT';
    return 'OPEN';
  };
  const eventsByDay = liveEvents.reduce<Record<string, MorningEvent[]>>((acc, ev) => {
    (acc[ev.day] = acc[ev.day] || []).push(ev);
    return acc;
  }, {});
  const dayRows: { day: string; label: string; events: MorningEvent[] }[] = [
    { day: 'Wednesday', label: 'Free Intro to Padel', events: eventsByDay['Wednesday'] || [] },
    { day: 'Thursday', label: 'Free Intro to Padel', events: eventsByDay['Thursday'] || [] },
    { day: 'Friday', label: 'Morning Open Play', events: eventsByDay['Friday'] || [] },
    { day: 'Saturday', label: 'Free Intro to Padel', events: eventsByDay['Saturday'] || [] },
  ];

  return (
    <div className="home-v2 morning-page">

      <SubPageNav ctaLabel="Register" ctaHref="#morning-schedule" onCtaClick={handleHeroCta} active="mornings" />

      <section className="v2-page-hero">
        <div className="v2-container">
          <div className="kicker">Mornings at Tempo</div>
          <h1 id="hero-title">
            Start your day <em>on glass.</em>
          </h1>
          <p className="lede">
            A weekly rhythm of open play and free intro slots — Wednesday, Thursday,
            Friday, and Saturday. Beat the Houston heat, meet the regulars, and find
            your tempo before the workday begins.
          </p>
          <div className="v2-hero-actions" style={{ marginTop: 32 }}>
            <button type="button" className="v2-btn v2-btn-on-light" onClick={handleHeroCta}>
              Reserve My Morning Spot
              <ArrowIcon />
            </button>
            <button type="button" className="v2-btn v2-btn-ghost" onClick={handleHeroSecondary}>
              See the Schedule
            </button>
          </div>
        </div>
      </section>

      {/* WEEKLY SCHEDULE */}
      <section className="v2-section noborder" id="morning-schedule" aria-labelledby="schedule-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">01 /</span>
              <span className="label">Weekly Schedule</span>
            </div>
            <h2 id="schedule-title">
              This <em>week.</em>
            </h2>
          </div>

          <div className="v2-week">
            {dayRows.map((row) => (
              <div className="slot-row" key={row.day}>
                <div className="day-cell">
                  <span className="dlabel">{row.day}</span>
                  <span className="ddate">{row.day.slice(0, 3)}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>{row.label}</span>
                </div>
                {row.events.length === 0 ? (
                  <div className="empty">
                    No morning sessions scheduled · Courts open for standard booking from 7am
                  </div>
                ) : (
                  <div className="slots-cell">
                    {row.events.map((ev) => {
                      const isFull = ev.status === 'sold_out' || ev.status === 'waitlist';
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          className="v2-mslot"
                          onClick={() => openRegistration(ev)}
                        >
                          <span className="t">{ev.time}</span>
                          <span className="label">{ev.name}</span>
                          <div className="meta">
                            <span>{(ev.endHour * 60 + ev.endMin) - (ev.startHour * 60 + ev.startMin)} MIN</span>
                            <span className={`status ${statusClass(ev)}`}>
                              {isFull ? 'WAITLIST' : statusText(ev)}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 32,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 20,
              flexWrap: 'wrap',
            }}
          >
            <p style={{ maxWidth: '60ch', color: 'var(--muted)', fontSize: 15, margin: 0 }}>
              All times are local to Houston. All morning sessions are outdoors. Coffee, water, and towels are in the
              lounge. Free parking on-site.
            </p>
            <button
              type="button"
              className="v2-btn v2-btn-on-light"
              onClick={() => {
                const first = liveEvents[0];
                if (first) openRegistration(first);
              }}
            >
              Register
              <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* WHY MORNINGS */}
      <section className="v2-section" aria-labelledby="why-mornings-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">02 /</span>
              <span className="label">Why Mornings</span>
            </div>
            <h2 id="why-mornings-title">
              Move before the day <em>moves</em> you.
            </h2>
          </div>

          <div className="v2-pillars" style={{ padding: 0 }}>
            <div className="v2-pillar">
              <div className="idx">A. BEFORE WORK</div>
              <h3>
                Start early.<span className="sub">— one hour on glass</span>
              </h3>
              <p>
                One hour of play in the morning sets the tone for everything after.
                Beat the Houston heat and the traffic.
              </p>
            </div>
            <div className="v2-pillar">
              <div className="idx">B. MEET REGULARS</div>
              <h3>
                Build a crew.<span className="sub">— without committing nights</span>
              </h3>
              <p>
                Real connections over sport while keeping your evenings free. The
                morning regulars are the soul of the club.
              </p>
            </div>
            <div className="v2-pillar">
              <div className="idx">C. STAY ON SCHEDULE</div>
              <h3>
                Train, shower, go.<span className="sub">— day on track</span>
              </h3>
              <p>
                Sauna, cold plunge, showers, coffee. After drop-off, before errands,
                calls, or the rest of the day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS PADEL */}
      <section className="v2-section" aria-labelledby="padel-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">03 /</span>
              <span className="label">What Is Padel?</span>
            </div>
            <h2 id="padel-title">
              Tennis meets squash. <em>Easier</em> to start.
            </h2>
          </div>

          <div className="v2-intro-grid">
            <div className="v2-steps">
              <div className="v2-step">
                <span className="n">01</span>
                <span className="t">
                  Easy to learn
                  <span>Short, social rallies from your very first session.</span>
                </span>
                <span className="time">DAY 1</span>
              </div>
              <div className="v2-step">
                <span className="n">02</span>
                <span className="t">
                  Glass walls in play
                  <span>Bounce off the back wall — the game stays alive.</span>
                </span>
                <span className="time">ALWAYS</span>
              </div>
              <div className="v2-step">
                <span className="n">03</span>
                <span className="t">
                  Always doubles
                  <span>Four players. Built for meeting people.</span>
                </span>
                <span className="time">2 vs 2</span>
              </div>
              <div className="v2-step">
                <span className="n">04</span>
                <span className="t">
                  Great workout
                  <span>60 minutes of movement that doesn't feel like a workout.</span>
                </span>
                <span className="time">60 MIN</span>
              </div>
            </div>
            <div className="visual">
              <img src={playerImg} alt="Player hitting a padel ball on TEMPO's glass-walled court" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="v2-section" id="faq-title-section" aria-labelledby="faq-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">04 /</span>
              <span className="label">FAQ</span>
            </div>
            <h2 id="faq-title">
              Before <em>you</em> come.
            </h2>
          </div>
          <div className="v2-faq-grid">
            {MORNING_FAQ.map((faq, idx) => (
              <FaqItem
                key={idx}
                id={`faq-${idx}`}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaqIndex === idx}
                onToggle={() => handleFaqToggle(idx, faq.question)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="v2-section"
        id="final-cta-title-section"
        style={{ background: 'var(--paper)' }}
        aria-labelledby="final-cta-title"
      >
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">05 /</span>
              <span className="label">Reserve</span>
            </div>
            <h2 id="final-cta-title">
              Pick your <em>morning.</em>
            </h2>
          </div>

          <div className="v2-slot-grid">
            {liveEvents.map((ev) => {
              const isFull = ev.status === 'sold_out' || ev.status === 'waitlist';
              return (
                <button
                  key={ev.id}
                  type="button"
                  className="v2-slot"
                  onClick={() => openRegistrationFromFinalCta(ev)}
                >
                  <span className="d">{ev.day}</span>
                  <span className="t">{ev.time}</span>
                  <span className="s">{isFull ? 'Join waitlist' : ev.ctaText}</span>
                </button>
              );
            })}
          </div>

          <p style={{ marginTop: 28, color: 'var(--muted)', fontSize: 14 }}>
            Questions? Call us at{' '}
            <a
              href={`tel:${MORNING_CONFIG.phone}`}
              onClick={() => handlePhoneClick('final_cta')}
              style={{ textDecoration: 'underline' }}
            >
              {MORNING_CONFIG.phone}
            </a>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <SubPageFoot />

      {/* Sticky mobile CTA */}
      <div className="v2-sticky-cta">
        <button type="button" className="v2-btn v2-btn-on-light" onClick={handleStickyCta}>
          Reserve My Morning Spot
          <ArrowIcon />
        </button>
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={selectedEvent}
        onCapacityUpdate={handleCapacityUpdate}
      />
    </div>
  );
};

export default MorningLandingPage;
